import type React from "react";
import { useEffect } from "react";

import { useTranslation } from "react-i18next";
import styled from "styled-components";

import type { UserCourseExercise } from "api/schemas/UserCourse";
import { MUISelect } from "shared/atoms/MUI";

import { Counter } from "./Counter";
import type { ExerciseStatus } from "./UserCourseExercise";

const createOption = (i: number) => ({ label: String(i), value: String(i) });

const createOptions = (start: number, stop: number) => {
  const a = [];
  for (let i = start; i <= stop; i += 1) {
    a.push(createOption(i));
  }
  return a;
};

interface Props {
  status: ExerciseStatus;
  exercise: UserCourseExercise;
  currentProtocolWeek: number;
  onChangeLevel: (exerciseId: number, level: number) => void;
  onChangeWeek: (id: number, week: number) => void;
  onChangeEndWeek: (id: number, week: number | null) => void;
}

export const ExerciseEditRow: React.FC<Props> = ({
  status,
  exercise,
  currentProtocolWeek,
  onChangeLevel,
  onChangeWeek,
  onChangeEndWeek,
}) => {
  const { id, current_level: currentLevel, introduction_protocol_week: week, end_protocol_week: endWeek } = exercise;

  const { t } = useTranslation();

  const numberOfLevels = exercise.levels.length;
  const expired = status === "expired";

  const startFirstWeek = Math.min(week, currentProtocolWeek);
  const endFirstWeek = Math.max(week, currentProtocolWeek);

  const weekOptions = createOptions(startFirstWeek, currentProtocolWeek + 16);
  const endWeekOptions = [{ label: `\u221E`, value: "" }, ...createOptions(startFirstWeek, endFirstWeek + 15)];

  useEffect(() => {
    if (endWeek && endWeek < week) {
      onChangeEndWeek(id, week);
    }
  }, [week, endWeek]);

  return (
    <ButtonRow>
      <GrowColumn>
        <Label>{t("usercourse.level")}</Label>
        <Counter
          value={currentLevel}
          maxValue={numberOfLevels}
          minValue={1}
          clickCallback={v => onChangeLevel(id, v)}
          disabled={expired}
        />
      </GrowColumn>
      <GrowColumn>
        <Label>{t("usercourse.start_week")}</Label>
        <MUISelect
          value={week}
          options={weekOptions}
          onChange={event => {
            onChangeWeek(id, Number(event.target.value as string));
          }}
          onClick={event => {
            event.stopPropagation();
          }}
        />
      </GrowColumn>
      <GrowColumn>
        <Label>{t("usercourse.end_week")}</Label>
        <MUISelect
          value={endWeek}
          options={endWeekOptions}
          onChange={event => {
            onChangeEndWeek(id, Number(event.target.value as string));
          }}
          onClick={event => {
            event.stopPropagation();
          }}
        />
      </GrowColumn>
    </ButtonRow>
  );
};

const Label = styled.div`
  color: ${props => props.theme.colors.redesign.db60};
  font-size: 12px;
  line-height: 16px;
`;

const GrowColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 8px;
`;
